import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import AthleteUpgradeModal from "../Dashboard/AthleteUpgradeModal";
import AthleteHomeMobile from "./mobile/AthleteHomeMobile";
import { fetchAthleteHome } from "./athleteServices";
import { EMPTY_VALUE, fmtMoney, HOME_ROW_STATUS, athleteDealAction } from "./athleteUtils";
import { fmtDate, greeting, timeAgo } from "../../ForBrands/brandDealUtils";
import { usePhoneTheme } from "../../ForBrands/useIsPhone";
import {
  Avatar,
  Badge,
  Btn,
  Card,
  ErrorState,
  InsightBox,
  Loading,
  MetricCard,
  Overline,
  PageHeader,
} from "../../ForBrands/ui/BrandUI";
import iconInsight from "../../../assets/brand/icon-insight.svg";

// Athlete Mode · Home (Figma 01 · Home): greeting + profile completion,
// KPI strip, the attention queue, deal activity analysis, upcoming payouts
// and the deals table. Every number comes from the athlete's own deals.
// On phones (below 640px, in the Athlete Mode shell) the same rows render
// as the mobile Home (Figma 2000:305) instead: AthleteHomeMobile.

const AttentionRow = ({ item }) => {
  const navigate = useNavigate();
  return (
    <div className="flex items-center justify-between gap-4 border-b border-bm-mutedbg py-[14px] last:border-b-0">
      <div className="flex min-w-0 flex-col gap-[2px]">
        <div className="bm-h-xs text-bm-ink">{item.title}</div>
        <div className="bm-body-m text-bm-muted">
          {item.meta}
          {item.at && item.kind !== "deliverable" ? ` · ${timeAgo(item.at)}` : ""}
        </div>
      </div>
      <Btn variant="secondary" size="sm" onClick={() => item.href && navigate(item.href)}>
        {item.action}
      </Btn>
    </div>
  );
};

const PAYOUT_BADGE = {
  pending: { label: "Pending approval", tone: "outline" },
  scheduled: { label: "Scheduled", tone: "outline" },
  awaiting_signature: { label: "Awaiting signature", tone: "outline" },
};

const ROW_STATUS_TONE = {
  in_progress: "outline",
  in_contract: "outline",
  completed: "outline",
  closed: "outline",
  declined: "passive",
  pending: "outline",
  in_review: "dark",
};

// ------------------------------------------ Display rows for both trees
const homeSubtitle = (needsCount, completion) =>
  needsCount
    ? `${needsCount} thing${needsCount === 1 ? "" : "s"} need${needsCount === 1 ? "s" : ""} you today. Your profile is ${completion}% complete.`
    : `Nothing needs you right now. Your profile is ${completion}% complete.`;

const homeKpis = (summary) => [
  { key: "attention", label: "Needs your attention", value: summary.attentionCount, sub: summary.attentionSub },
  { key: "earned", label: `Earned in ${summary.year}`, value: fmtMoney(summary.earnedThisYear), sub: "paid directly to your bank" },
  {
    key: "pending",
    label: "Pending payouts",
    value: fmtMoney(summary.pendingPayouts),
    sub: `across ${summary.activeDeals} active deal${summary.activeDeals === 1 ? "" : "s"}`,
  },
  { key: "atlas", label: "Atlas matches", value: summary.atlasMatches, sub: summary.atlasMatches ? "new opportunities" : "link socials to get matched" },
  {
    key: "due",
    label: "Deliverables due",
    value: summary.deliverablesDue,
    sub: summary.nextDue ? `next due ${fmtDate(summary.nextDue, { year: false })}` : "nothing scheduled",
  },
];

const activityTiles = (activity, summary) => [
  {
    label: "Avg. deal value",
    value: activity.avgDealValue == null ? EMPTY_VALUE : fmtMoney(activity.avgDealValue),
    sub: activity.median ? `vs ${fmtMoney(activity.median)} median` : "no sport median yet",
  },
  {
    label: "On-time delivery",
    value: activity.onTimeRate == null ? EMPTY_VALUE : `${activity.onTimeRate}%`,
    sub: activity.approvedDeliverables
      ? `${activity.approvedDeliverables} deliverable${activity.approvedDeliverables === 1 ? "" : "s"} approved`
      : "no approved deliverables yet",
  },
  {
    label: `${summary.year} tax status`,
    value: fmtMoney(activity.taxYearTotal),
    sub: activity.needs1099 ? `1099 at ${fmtMoney(activity.taxThreshold)}` : `1099 above ${fmtMoney(activity.taxThreshold)}`,
  },
];

const payoutRows = (payouts) =>
  (payouts || []).map((p, i) => ({
    key: `${p.dealId}-${i}`,
    label: p.label,
    brand: p.brand,
    due: p.dueDate === "on signature" ? "on signature" : fmtDate(p.dueDate, { year: false }),
    amount: fmtMoney(p.amount, { cents: true }),
    href: p.status === "awaiting_signature" ? `/athlete/deals/${p.dealId}/contract` : `/athlete/deals/${p.dealId}`,
    status: p.status,
    statusLabel: PAYOUT_BADGE[p.status]?.label || p.status,
    statusTone: PAYOUT_BADGE[p.status]?.tone || "outline",
  }));

const dealRows = (deals) =>
  deals.map((d) => ({
    id: d.id,
    initials: d.campaignInitials || d.brand?.initials,
    company: d.brand?.company,
    campaign: d.campaignName,
    statusTone: ROW_STATUS_TONE[d.rowStatus] || "outline",
    statusLabel: HOME_ROW_STATUS[d.rowStatus] || d.statusLabel,
    value: fmtMoney(d.value),
    received: fmtMoney(d.received),
    nextPayout: d.nextPayout
      ? `${fmtMoney(d.nextPayout.amount)}${d.nextPayout.dueDate ? ` (${fmtDate(d.nextPayout.dueDate, { year: false })})` : ""}`
      : EMPTY_VALUE,
    deliverables: d.deliverablesTotal ? `${d.deliverablesDone} of ${d.deliverablesTotal} done` : EMPTY_VALUE,
    action: athleteDealAction(d),
  }));

// The tablet and desktop tree.
const HomeDesktop = ({ title, subtitle, kpis, tiles, completion, attention, activity, payouts, deals }) => {
  const navigate = useNavigate();
  const logTarget = attention.find((a) => a.kind === "deliverable");
  const [attentionKpi, ...otherKpis] = kpis;

  return (
    <>
      <PageHeader
        eyebrow="Athlete Mode"
        title={title}
        subtitle={subtitle}
        actions={
          <>
            {completion < 100 && (
              <Btn variant="secondary" onClick={() => navigate("/athlete/profile/edit")}>
                Complete profile
              </Btn>
            )}
            <Btn onClick={() => navigate(logTarget ? logTarget.href : "/athlete/deals")}>Log content</Btn>
          </>
        }
      />

      <div className="flex flex-col gap-4 pt-2">
        {/* KPI strip */}
        <div className="flex flex-wrap gap-3">
          <MetricCard muted label={attentionKpi.label} value={attentionKpi.value} sub={attentionKpi.sub} className="!flex-none min-w-[230px]" />
          {otherKpis.map((k) => (
            <MetricCard key={k.key} label={k.label} value={k.value} sub={k.sub} />
          ))}
        </div>

        {/* Needs your attention */}
        <Card padding="p-[22px]" className="flex flex-col gap-[6px]">
          <div className="flex items-center justify-between pb-[6px]">
            <Overline>Needs your attention</Overline>
            <span className="bm-body-s text-bm-tertiary">Sorted by urgency</span>
          </div>
          {attention.length ? (
            <div>
              {attention.map((item, i) => (
                <AttentionRow key={`${item.kind}-${item.href || i}`} item={item} />
              ))}
            </div>
          ) : (
            <p className="py-6 text-center bm-body-m text-bm-muted">You're all caught up.</p>
          )}
        </Card>

        {/* Deal activity + upcoming payouts */}
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start">
          <InsightBox className="min-w-0 flex-1" title="Your deal activity" tiles={tiles}>
            {activity.narrative}
          </InsightBox>

          <Card padding="px-[22px] pb-[10px] pt-[22px]" className="flex w-full flex-col lg:w-[420px]">
            <Overline className="pb-2">Upcoming payouts · direct to your bank</Overline>
            {payouts.length ? (
              payouts.map((p) => (
                <button
                  type="button"
                  key={p.key}
                  onClick={() => navigate(p.href)}
                  className="flex items-start justify-between gap-3 border-b border-bm-mutedbg py-[14px] text-left last:border-b-0 hover:opacity-80"
                >
                  <span className="flex min-w-0 flex-col gap-[2px]">
                    <span className="bm-h-xs text-bm-ink">{p.label}</span>
                    <span className="bm-body-s text-bm-muted">
                      {p.brand} · {p.due}
                    </span>
                  </span>
                  <span className="flex shrink-0 flex-col items-end gap-[6px]">
                    <span className="bm-num-m text-bm-ink">{p.amount}</span>
                    <Badge tone={p.statusTone}>{p.statusLabel}</Badge>
                  </span>
                </button>
              ))
            ) : (
              <p className="py-4 bm-body-m text-bm-muted">Payouts from signed deals will show up here.</p>
            )}
          </Card>
        </div>

        {/* Deals table */}
        <Card padding="p-0" className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[960px]">
              <thead>
                <tr className="bg-bm-subtle">
                  <th className="bm-th pl-4 text-left">Brand / campaign</th>
                  <th className="bm-th text-left">Status</th>
                  <th className="bm-th text-left">Value</th>
                  <th className="bm-th text-left">Received</th>
                  <th className="bm-th text-left">Next payout</th>
                  <th className="bm-th text-left">Deliverables</th>
                  <th className="bm-th pr-4 text-left">Actions</th>
                </tr>
              </thead>
              <tbody>
                {deals.length === 0 && (
                  <tr>
                    <td colSpan={7} className="px-4 py-8 text-center bm-body-m text-bm-muted">
                      Offers from brands will land here. Keep your profile complete so brands can find you in Discover.
                    </td>
                  </tr>
                )}
                {deals.map((d) => (
                  <tr key={d.id} className="border-t border-bm-border">
                    <td className="bm-td pl-4">
                      <span className="flex items-center gap-3">
                        <Avatar initials={d.initials} tone="dark" square size={40} />
                        <span className="flex min-w-0 flex-col">
                          <span className="bm-h-xs text-bm-ink">{d.company}</span>
                          <span className="bm-body-s text-bm-muted">{d.campaign}</span>
                        </span>
                      </span>
                    </td>
                    <td className="bm-td">
                      <Badge tone={d.statusTone}>{d.statusLabel}</Badge>
                    </td>
                    <td className="bm-td bm-num-m text-bm-ink">{d.value}</td>
                    <td className="bm-td text-bm-ink">{d.received}</td>
                    <td className="bm-td text-bm-ink">{d.nextPayout}</td>
                    <td className="bm-td text-bm-ink">{d.deliverables}</td>
                    <td className="bm-td pr-4">
                      <Btn variant="secondary" size="sm" onClick={() => navigate(d.action.href)}>
                        {d.action.label}
                      </Btn>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>

        {/* Closing insight */}
        <Card padding="px-[22px] py-5" className="flex items-start gap-3">
          <img src={iconInsight} alt="" className="mt-[2px] h-5 w-5" />
          <div className="flex flex-col gap-2">
            <Overline>Your deal activity</Overline>
            <p className="bm-body-m text-bm-muted">
              {activity.onTimeRate === 100
                ? `Excellent! You maintain a 100% on-time delivery rate across your deliverables. `
                : activity.onTimeRate !== null
                  ? `Your on-time delivery rate is ${activity.onTimeRate}%. Brands weigh this when they compare athletes. `
                  : "Brands see your on-time delivery rate once you complete your first deliverable. "}
              {activity.avgDealValue !== null
                ? `Your average deal value is ${fmtMoney(activity.avgDealValue)}${activity.median ? `, ${activity.avgDealValue >= activity.median ? "above" : "below"} the ${fmtMoney(activity.median)} median for your sport` : ""}. `
                : ""}
              {logTarget ? `${logTarget.title} will move ${logTarget.meta.split(" · ")[0]} forward.` : ""}
            </p>
          </div>
        </Card>
      </div>
    </>
  );
};

const AthleteHomePage = () => {
  const phone = usePhoneTheme();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const entitlement = useSelector((state) => state.entitlement?.entitlement || "public");

  // Sign-in upgrade pitch for free athletes, once per browser session.
  const [showUpgrade, setShowUpgrade] = useState(false);
  useEffect(() => {
    if (entitlement === "athlete_free" && !sessionStorage.getItem("athleteUpgradeSeen")) {
      setShowUpgrade(true);
      sessionStorage.setItem("athleteUpgradeSeen", "1");
    }
  }, [entitlement]);

  useEffect(() => {
    fetchAthleteHome()
      .then((payload) => {
        setData(payload);
        setError(null);
      })
      .catch((err) => setError(err.message || "Could not load your home view"));
  }, []);

  if (error) {
    return (
      <div className="py-10">
        <ErrorState>{error}</ErrorState>
      </div>
    );
  }
  if (!data) return <Loading />;

  const { profile, completion, summary, attention, activity, upcomingPayouts, deals } = data;
  const title = `${greeting()}, ${profile?.firstName || "there"}`;
  const subtitle = homeSubtitle(summary.attentionCount, completion);
  const kpis = homeKpis(summary);
  const tiles = activityTiles(activity, summary);
  const payouts = payoutRows(upcomingPayouts);
  const dealList = dealRows(deals);

  // The modal keeps its place, so crossing 640px leaves it open.
  return (
    <div>
      <AthleteUpgradeModal open={showUpgrade} onClose={() => setShowUpgrade(false)} />
      {phone ? (
        <AthleteHomeMobile
          title={title}
          subtitle={subtitle}
          kpis={kpis}
          narrative={activity.narrative}
          tiles={tiles}
          attention={attention}
          payouts={payouts}
          deals={dealList}
        />
      ) : (
        <HomeDesktop
          title={title}
          subtitle={subtitle}
          kpis={kpis}
          tiles={tiles}
          completion={completion}
          attention={attention}
          activity={activity}
          payouts={payouts}
          deals={dealList}
        />
      )}
    </div>
  );
};

export default AthleteHomePage;
